import { Feature, Map, View } from "ol";
import { fromLonLat, toLonLat, transformExtent } from "ol/proj";
import { useEffect, useRef, useState } from "react";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Attribution } from "ol/control";
import MapContext from "./MapContext";
import MapTilerLayer from "./MapTileLayer";
import { Maps } from "./MapTileLayer";
import Geolocation from "ol/Geolocation.js";

import "./Map.scss";
import "ol/ol.css";
import Point from "ol/geom/Point.js";
import { Circle, Fill, Stroke, Style } from "ol/style.js";

export enum VECTOR_LAYERS {
  FIELDS = "fields-layer",
  FIELDS_MARKERS = "fields-layer-marker",
}

const positionFeature = new Feature();
positionFeature.setStyle(
  new Style({
    image: new Circle({
      radius: 7,
      fill: new Fill({
        color: "#ff0000",
      }),
      stroke: new Stroke({
        color: "rgba(255, 50, 0, 0.4)",
        width: 25,
      }),
    }),
  })
);

export const fieldsVectorLayer = new VectorLayer({
  zIndex: 30,
  properties: {
    name: VECTOR_LAYERS.FIELDS,
  },
  source: new VectorSource({
    features: [positionFeature],
  }),
});

export const fieldsMarkerVectorLayer = new VectorLayer({
  zIndex: 30,
  properties: {
    name: VECTOR_LAYERS.FIELDS_MARKERS,
  },
  source: new VectorSource({
    features: [],
  }),
});

const geolocation = new Geolocation({
  tracking: true,
  trackingOptions: {
    enableHighAccuracy: true,
  },
});

// update the HTML page when the position changes.
geolocation.on("change:position", function () {
  let coordinates = geolocation.getPosition();
  positionFeature.setGeometry(
    coordinates ? new Point(fromLonLat(coordinates)) : undefined
  );
});

interface MapProps {
  children?: any;
  zoom?: number;
  center?: number[];
  bbox?: number[];
  initialCenter?: number[];
  activelayer?: Maps;
  trackPosition?: boolean;
}

function MapView(props: MapProps) {
  const mapRef = useRef<any>(null);
  const [map, setMap] = useState<Map>();
  const [zoom, setZoom] = useState<number>();
  const [center, setCenter] = useState<number[] | undefined>(
    props.center || props.initialCenter || geolocation.getPosition()
  );
  useEffect(() => {
    const extent = props.bbox
      ? transformExtent(props.bbox, "EPSG:4326", "EPSG:3857")
      : undefined;
    let mapObject = new Map({
      view: new View({
        extent,
        showFullExtent: true,
      }),
      controls: [
        new Attribution({
          collapsible: false,
        }),
      ],
      layers: [fieldsVectorLayer, fieldsMarkerVectorLayer],
    });
    setMap(mapObject);
    if (extent) {
      mapObject?.getView().fit(extent);
    }
    return () => setMap(undefined);
  }, [props.bbox]);
  useEffect(() => {
    if (map && mapRef?.current) {
      map?.setTarget(mapRef.current);
      mapRef?.current?.focus();
      return () => map.setTarget(undefined);
    }
  }, [mapRef, map]);
  useEffect(() => {
    if (props.zoom) setZoom(props.zoom);
    if (props.center) {
      const [lon, lat] = props.center;
      if (!isNaN(lon) && !isNaN(lat) && (lon !== 0 || lat !== 0)) {
        setCenter(props.center);
      }
    }
  }, [props.zoom, props.center]);

  useEffect(() => {
    if (map && zoom !== undefined) {
      map?.getView()?.setZoom(zoom);
    }
  }, [map, zoom]);
  useEffect(() => {
    if (map && center !== undefined) {
      const [lon, lat] = center;
      if (!isNaN(lon) && !isNaN(lat) && (lon !== 0 || lat !== 0)) {
        map?.getView().setCenter(fromLonLat(center));
      }
    }
  }, [map, center]);
  useEffect(() => {
    if (!props.trackPosition) return;
    const previousZoom = sessionStorage.getItem("previousZoom");
    if (previousZoom) {
      map?.getView()?.setZoom(parseInt(previousZoom));
    }
    let previousCenter = sessionStorage.getItem("previousCenter");
    if (previousCenter) setCenter(JSON.parse(previousCenter));

    const view = map?.getView();
    const handleCenterChange = () => {
      let previousCenterString = sessionStorage.getItem("previousCenter");
      let previousCenter = previousCenterString
        ? JSON.parse(previousCenterString)
        : [];
      const newCenter = toLonLat(view?.getCenter() as number[]).map((coord) =>
        parseFloat(coord.toFixed(4))
      );
      if (
        !previousCenter ||
        previousCenter?.length === 0 ||
        newCenter[0] !== previousCenter[0] ||
        newCenter[1] !== previousCenter[1]
      ) {
        sessionStorage.setItem("previousCenter", JSON.stringify(newCenter));
        sessionStorage.setItem("previousZoom", JSON.stringify(view?.getZoom()));
      }
    };
    view?.on("change:center", handleCenterChange);
    return () => view?.un("change:center", handleCenterChange);
  }, [map, props.trackPosition]);
  return (
    <MapContext.Provider value={{ map, center, setCenter, zoom, setZoom }}>
      <div
        className="map"
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
      <MapTilerLayer style={props.activelayer || Maps.OSM} />
      {props?.children}
    </MapContext.Provider>
  );
}

export default MapView;
